import React, { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { RoleApi } from "@/api";

interface Permission {
  id: number;
  permissionCode: string;
  permissionName: string;
  permissionType: string;
  featureVO: {
    id: number;
    featureCode: string;
    featureName: string;
  };
}

interface FeaturePermissions {
  featureId: number;
  featureCode: string;
  featureName: string;
  createPermission?: Permission;
  viewPermission?: Permission;
  updatePermission?: Permission;
  deletePermission?: Permission;
}

interface Role {
  id: string;
  name: string;
  description: string;
  users?: number;
  permissions?: FeaturePermissions[];
}

interface RolePermissionsSheetProps {
  role: Role;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPermissionsSaved?: () => void;
  possiblePermissions: {
    featureId: number;
    featureCode: string;
    featureName: string;
  }[];
}

const modules = [
  {
    id: "users",
    name: "Users & Access",
    description: "Management of users and roles",
  },
  {
    id: "programs",
    name: "Loyalty Programs",
    description: "Create and manage loyalty programs",
  },
  {
    id: "merchants",
    name: "Merchants",
    description: "Manage merchant accounts and locations",
  },
  {
    id: "transactions",
    name: "Transactions",
    description: "View and manage transaction history",
  },
  {
    id: "settings",
    name: "System Settings",
    description: "Configure system preferences and settings",
  },
];

export const RolePermissionsSheet: React.FC<RolePermissionsSheetProps> = ({
  role,
  open,
  onOpenChange,
  onPermissionsSaved,
  possiblePermissions,
}) => {
  const [roleName, setRoleName] = useState(role.name);
  const [roleDescription, setRoleDescription] = useState(role.description);
  // Use permissions directly from props to always reflect the selected role
  const permissions: FeaturePermissions[] = role.permissions || [];

  // Track enabled/disabled state for each permission by permission id
  const [enabledPermissions, setEnabledPermissions] = useState<
    Record<string, boolean>
  >({});

  // Track original permissions state to compare changes
  const [originalPermissions, setOriginalPermissions] = useState<
    Record<string, boolean>
  >({});

  // Helper: get permission object for a feature and type from role.permissions
  const getRolePermission = (featureId: number, type: string) => {
    const feature = permissions.find((f) => f.featureId === featureId);
    if (!feature) return undefined;
    if (type === "create") return feature.createPermission;
    if (type === "view") return feature.viewPermission;
    if (type === "update") return feature.updatePermission;
    if (type === "delete") return feature.deletePermission;
    return undefined;
  };

  // Helper: get permission code from possiblePermissions for a feature and type
  const getPossiblePermissionCode = (feature: any, type: string) => {
    if (type === "create") return feature.createPermissionCode;
    if (type === "view") return feature.viewPermissionCode;
    if (type === "update") return feature.updatePermissionCode;
    if (type === "delete") return feature.deletePermissionCode;
    return undefined;
  };

  // Sync enabledPermissions with role.permissions when role or permissions change
  useEffect(() => {
    const initial: Record<string, boolean> = {};
    possiblePermissions.forEach((feature) => {
      ["create", "view", "update", "delete"].forEach((type) => {
        const perm = getRolePermission(feature.featureId, type);
        if (perm) {
          initial[perm.id.toString()] = true;
        }
      });
    });
    setEnabledPermissions(initial);
    setOriginalPermissions(initial);
  }, [role, permissions, possiblePermissions]);

  const handlePermissionChange = async (
    permissionId: number,
    checked: boolean,
    featureId?: number,
    action?: string
  ) => {
    const key =
      permissionId > 0 ? permissionId.toString() : `${featureId}-${action}`;

    // Update UI immediately
    setEnabledPermissions((prev) => ({
      ...prev,
      [key]: checked,
    }));

    // Find the permission code
    let permissionCode = "";
    if (permissionId > 0) {
      // Real permission ID
      const permission = permissions
        .flatMap((feature) => [
          feature.viewPermission,
          feature.createPermission,
          feature.updatePermission,
          feature.deletePermission,
        ])
        .find((perm) => perm?.id === permissionId);
      if (permission) {
        permissionCode = permission.permissionCode;
      }
    } else if (featureId && action) {
      // Use possiblePermissions to get the permission code
      const feature = possiblePermissions.find(
        (f) => f.featureId === featureId
      );
      if (feature) {
        permissionCode = getPossiblePermissionCode(feature, action);
      }
    }

    if (permissionCode) {
      try {
        if (checked) {
          // Add permission
          await RoleApi.addPermissionsToRole({
            roleId: Number(role.id),
            permissionCodesToBeAdded: [permissionCode],
          });
          toast.success(`Permission ${permissionCode} added successfully`);
        } else {
          // Remove permission
          await RoleApi.deletePermissionsFromRole({
            roleId: Number(role.id),
            permissionCodesToBeDeleted: [permissionCode],
          });
          toast.success(`Permission ${permissionCode} removed successfully`);
        }

        // Refresh permissions from backend
        const res = await RoleApi.getPermissionsUsingRoleId(role.id);
        const updatedPermissions = Array.isArray(res.data) ? res.data : [];

        // Update the original permissions state with the new data
        const newOriginalPermissions: Record<string, boolean> = {};

        updatedPermissions.forEach((feature: any) => {
          [
            feature.viewPermission,
            feature.createPermission,
            feature.updatePermission,
            feature.deletePermission,
          ].forEach((perm: any) => {
            if (perm) {
              newOriginalPermissions[perm.id.toString()] = true;
            }
          });
        });

        setOriginalPermissions(newOriginalPermissions);

        // For add operations, ensure the switch stays ON
        if (checked) {
          // Keep the current state but ensure the added permission is enabled
          const updatedEnabledPermissions = { ...enabledPermissions };

          // If this was a synthetic key, keep it enabled
          if (featureId && action) {
            const syntheticKey = `${featureId}-${action}`;
            updatedEnabledPermissions[syntheticKey] = true;
          }

          // Also enable the real permission ID if it exists in the response
          const feature = updatedPermissions.find(
            (f: any) => f.featureId === featureId
          );
          if (feature) {
            const permKey = `${action}Permission` as keyof any;
            const perm = feature[permKey];
            if (perm) {
              updatedEnabledPermissions[perm.id.toString()] = true;
            }
          }

          setEnabledPermissions(updatedEnabledPermissions);
        } else {
          // For remove operations, use the backend state
          setEnabledPermissions(newOriginalPermissions);
        }
      } catch (e) {
        console.error("Error updating permission:", e);
        toast.error("Failed to update permission");

        // Revert the UI change on error
        setEnabledPermissions((prev) => ({
          ...prev,
          [key]: !checked,
        }));
      }
    }
  };

  const handleSave = async () => {
    // Just close the sheet since permissions are now handled individually
    onOpenChange(false);
    if (typeof onPermissionsSaved === "function") onPermissionsSaved();
  };

  // Show all features - users can enable/disable any permission
  const filteredPermissions = permissions;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md md:max-w-lg lg:max-w-xl">
        <SheetHeader>
          <SheetTitle>Edit Role</SheetTitle>
          <SheetDescription>
            Update role information and permissions
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="roleName" className="text-sm font-medium">
                Role Name
              </label>
              <Input
                id="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label htmlFor="roleDescription" className="text-sm font-medium">
                Description
              </label>
              <Input
                id="roleDescription"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-4">Role Permissions</h3>

            <Tabs defaultValue="modules">
              <TabsList className="mb-4">
                <TabsTrigger value="modules">By Module</TabsTrigger>
                <TabsTrigger value="actions">By Action Type</TabsTrigger>
              </TabsList>

              <TabsContent value="modules" className="space-y-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Feature</TableHead>
                      <TableHead className="w-1/6 text-center">View</TableHead>
                      <TableHead className="w-1/6 text-center">
                        Create
                      </TableHead>
                      <TableHead className="w-1/6 text-center">
                        Update
                      </TableHead>
                      <TableHead className="w-1/6 text-center">
                        Delete
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {possiblePermissions.map((feature) => (
                      <TableRow key={feature.featureId}>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {feature.featureName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {feature.featureCode}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={
                              !!enabledPermissions[
                                getRolePermission(
                                  feature.featureId,
                                  "view"
                                )?.id?.toString() || `${feature.featureId}-view`
                              ]
                            }
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                getRolePermission(feature.featureId, "view")
                                  ?.id || 0,
                                checked,
                                feature.featureId,
                                "view"
                              )
                            }
                            disabled={
                              !getPossiblePermissionCode(feature, "view")
                            }
                            title={
                              getRolePermission(feature.featureId, "view")
                                ?.permissionName || "View Permission"
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={
                              !!enabledPermissions[
                                getRolePermission(
                                  feature.featureId,
                                  "create"
                                )?.id?.toString() ||
                                  `${feature.featureId}-create`
                              ]
                            }
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                getRolePermission(feature.featureId, "create")
                                  ?.id || 0,
                                checked,
                                feature.featureId,
                                "create"
                              )
                            }
                            disabled={
                              !getPossiblePermissionCode(feature, "create")
                            }
                            title={
                              getRolePermission(feature.featureId, "create")
                                ?.permissionName || "Create Permission"
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={
                              !!enabledPermissions[
                                getRolePermission(
                                  feature.featureId,
                                  "update"
                                )?.id?.toString() ||
                                  `${feature.featureId}-update`
                              ]
                            }
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                getRolePermission(feature.featureId, "update")
                                  ?.id || 0,
                                checked,
                                feature.featureId,
                                "update"
                              )
                            }
                            disabled={
                              !getPossiblePermissionCode(feature, "update")
                            }
                            title={
                              getRolePermission(feature.featureId, "update")
                                ?.permissionName || "Update Permission"
                            }
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Switch
                            checked={
                              !!enabledPermissions[
                                getRolePermission(
                                  feature.featureId,
                                  "delete"
                                )?.id?.toString() ||
                                  `${feature.featureId}-delete`
                              ]
                            }
                            onCheckedChange={(checked) =>
                              handlePermissionChange(
                                getRolePermission(feature.featureId, "delete")
                                  ?.id || 0,
                                checked,
                                feature.featureId,
                                "delete"
                              )
                            }
                            disabled={
                              !getPossiblePermissionCode(feature, "delete")
                            }
                            title={
                              getRolePermission(feature.featureId, "delete")
                                ?.permissionName || "Delete Permission"
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="actions">
                <div className="space-y-6">
                  {["view", "create", "update", "delete"].map((action) => (
                    <div key={action} className="mb-6">
                      <h4 className="font-medium capitalize mb-2">
                        {action} Permissions
                      </h4>
                      <div className="bg-gray-50 rounded-md p-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Feature</TableHead>
                              <TableHead className="text-right">
                                Access
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {possiblePermissions.map((feature) => {
                              const permKey =
                                action === "view"
                                  ? "viewPermission"
                                  : action === "create"
                                  ? "createPermission"
                                  : action === "update"
                                  ? "updatePermission"
                                  : "deletePermission";
                              const perm = getRolePermission(
                                feature.featureId,
                                action
                              );
                              return (
                                <TableRow
                                  key={feature.featureId + "-" + action}
                                >
                                  <TableCell>
                                    <div>
                                      <div className="font-medium">
                                        {feature.featureName}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {perm?.permissionName ||
                                          `${
                                            action.charAt(0).toUpperCase() +
                                            action.slice(1)
                                          } Permission`}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Switch
                                      checked={
                                        !!enabledPermissions[
                                          perm?.id?.toString() ||
                                            `${feature.featureId}-${action}`
                                        ]
                                      }
                                      onCheckedChange={(checked) =>
                                        handlePermissionChange(
                                          perm?.id || 0,
                                          checked,
                                          feature.featureId,
                                          action
                                        )
                                      }
                                      disabled={
                                        !getPossiblePermissionCode(
                                          feature,
                                          action
                                        )
                                      }
                                      title={
                                        perm?.permissionName ||
                                        `${
                                          action.charAt(0).toUpperCase() +
                                          action.slice(1)
                                        } Permission`
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <SheetFooter>
          <Button onClick={handleSave}>Save Changes</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
